import { createPortal } from 'react-dom';
import { useState } from 'react';
import { X } from 'lucide-react';

interface Props {
  studentName: string;
  /** True when the student was already checked in — this is a correction, not a new mark. */
  isCorrection?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  isSaving?: boolean;
}

/**
 * The three reasons that account for nearly every after-school absence, offered as one-tap
 * presets so the common case costs a single extra tap. Anything else goes in as free text —
 * `absence_reason` is deliberately not an enum, so this list can change without a
 * migration and without invalidating rows already written.
 */
export const COMMON_ABSENCE_REASONS = [
  'Sick',
  'Went home with family',
  'Appointment or other activity',
] as const;

export function AbsenceReasonModal({ studentName, isCorrection, onConfirm, onClose, isSaving }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [freetext, setFreetext] = useState('');
  const [showFreetext, setShowFreetext] = useState(false);

  const reason = showFreetext ? freetext.trim() : (selected ?? '');

  // Portalled to `document.body` for the reason under `MOBILE_CHROME_ON_BODY` in
  // `StaffLayout`: the page renders inside pull-to-refresh's `overflow: hidden` wrappers, and
  // on iOS a clip in the ancestry keeps a fixed element out of the viewport-fixed layer that
  // the bottom nav and the voice button now live in. Left in the roster, this sheet painted
  // *under* both of them — its Confirm and Cancel buttons sat behind the nav — no matter what
  // z-index it carried, because the two are not in the same layer to compare within.
  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex flex-col justify-end sm:items-center sm:justify-center z-[60]"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-xl p-6 w-full sm:max-w-md sm:mx-4 space-y-4">
        <div className="sm:hidden flex justify-center -mt-2 mb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Mark Absent</h3>
            <p className="text-sm text-gray-500 mt-0.5">{studentName}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {isCorrection && (
          <p className="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
            This student is currently checked in. Marking them absent will clear that check-in.
          </p>
        )}

        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reason</p>
          <div className="space-y-1.5">
            {COMMON_ABSENCE_REASONS.map(option => (
              <button
                key={option}
                onClick={() => { setSelected(option); setShowFreetext(false); }}
                className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                  selected === option && !showFreetext
                    ? 'border-blue-500 bg-blue-50 text-blue-900 font-medium'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
                }`}
              >
                {option}
              </button>
            ))}
            <button
              onClick={() => { setShowFreetext(true); setSelected(null); }}
              className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                showFreetext
                  ? 'border-blue-500 bg-blue-50 text-blue-900 font-medium'
                  : 'border-gray-200 hover:border-gray-300 text-gray-500'
              }`}
            >
              Other…
            </button>
          </div>
        </div>

        {showFreetext && (
          <input
            type="text"
            autoFocus
            value={freetext}
            onChange={e => setFreetext(e.target.value)}
            placeholder="Enter reason"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason || isSaving}
            className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {isSaving ? 'Saving…' : 'Mark Absent'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
